import React, { useMemo } from "react";
import { FiExternalLink } from "react-icons/fi";
import {
  ContentMediaAttachment,
  parseVimeoId,
  parseYoutubeId,
  vimeoEmbedUrl,
  youtubeEmbedUrl,
} from "../../utils/contentMedia";
import { ContentPdfViewer } from "./ContentPdfViewer";

const ExternalI = FiExternalLink as React.FC<{ size?: number }>;

type Props = {
  media: ContentMediaAttachment;
  /** Aperçu compact dans le formulaire admin */
  compact?: boolean;
};

export const ContentMediaPreview: React.FC<Props> = ({ media, compact }) => {
  const { attachmentType, attachmentUrl, attachmentLayout = "auto" } = media;

  const youtubeId = useMemo(
    () => (attachmentType === "youtube" ? parseYoutubeId(attachmentUrl) : null),
    [attachmentType, attachmentUrl]
  );
  const vimeoId = useMemo(
    () => (attachmentType === "video" ? parseVimeoId(attachmentUrl) : null),
    [attachmentType, attachmentUrl]
  );

  const frameClass =
    attachmentLayout === "portrait"
      ? "content-media__frame content-media__frame--portrait"
      : attachmentLayout === "landscape"
        ? "content-media__frame content-media__frame--landscape"
        : "content-media__frame content-media__frame--auto";

  if (attachmentType === "youtube" && youtubeId) {
    return (
      <div className={`content-media ${compact ? "content-media--compact" : ""}`} data-testid="media-preview-youtube">
        <div className={`content-media__embed ${frameClass}`}>
          <iframe
            src={youtubeEmbedUrl(youtubeId)}
            title="Vidéo YouTube"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  if (attachmentType === "video") {
    if (vimeoId) {
      return (
        <div className={`content-media ${compact ? "content-media--compact" : ""}`} data-testid="media-preview-vimeo">
          <div className={`content-media__embed ${frameClass}`}>
            <iframe
              src={vimeoEmbedUrl(vimeoId)}
              title="Vidéo Vimeo"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      );
    }
    return (
      <div className={`content-media ${compact ? "content-media--compact" : ""}`} data-testid="media-preview-video">
        <div className={`content-media__embed ${frameClass}`}>
          <video src={attachmentUrl} controls playsInline preload="metadata" className="content-media__video" />
        </div>
        <a href={attachmentUrl} target="_blank" rel="noreferrer" className="content-media__open-link">
          <ExternalI size={14} /> Ouvrir la vidéo
        </a>
      </div>
    );
  }

  if (attachmentType === "pdf") {
    return (
      <div className={`content-media ${compact ? "content-media--compact" : ""}`} data-testid="media-preview-pdf">
        <ContentPdfViewer url={attachmentUrl} layout={attachmentLayout} />
        <a
          href={attachmentUrl}
          target="_blank"
          rel="noreferrer"
          className="content-media__open-link"
        >
          <ExternalI size={14} /> Ouvrir dans un nouvel onglet
        </a>
      </div>
    );
  }

  return null;
};
